/* eslint-disable import/no-cycle */
import { adminApi } from '@/includes/contentManagementApi';

export default {
  /**
   * Create a new dimension
   * @param {Object} dimensionData - The dimension data to create
   * @returns {Promise<Object>} The created dimension
   */
  createDimension(dimensionData) {
    return adminApi.post('/dimensions', dimensionData);
  },

  /**
   * Get all dimensions
   * @returns {Promise<Array>} Array of dimensions
   */
  getAllDimensions() {
    return adminApi.get('/dimensions');
  },

  /**
   * Get a dimension by ID
   * @param {string} id - The dimension ID
   * @returns {Promise<Object>} The dimension data
   */
  getDimensionById(id) {
    return adminApi.get(`/dimensions/${id}`);
  },

  /**
   * Get a dimension by value
   * @param {string} value - The dimension value/identifier
   * @returns {Promise<Object>} The dimension data
   */
  getDimensionByValue(value) {
    return adminApi.get(`/dimensions/value/${value}`);
  },

  /**
   * Update a dimension
   * @param {string} id - The dimension ID
   * @param {Object} dimensionData - The updated dimension data
   * @returns {Promise<Object>} The updated dimension
   */
  updateDimension(id, dimensionData) {
    return adminApi.patch(`/dimensions/${id}`, dimensionData);
  },

  /**
   * Delete a dimension
   * @param {string} id - The dimension ID
   * @returns {Promise<void>}
   */
  deleteDimension(id) {
    return adminApi.delete(`/dimensions/${id}`);
  },

  /**
   * Add a result description to a dimension
   * @param {string} id - The dimension ID
   * @param {Object} resultDescription - The result description data
   * @returns {Promise<Object>} The updated dimension
   */
  addResultDescription(id, resultDescription) {
    return adminApi.post(`/dimensions/${id}/result-descriptions`, resultDescription);
  },

  /**
   * Remove a result description from a dimension
   * @param {string} id - The dimension ID
   * @param {number} index - The index of the result description to remove
   * @returns {Promise<Object>} The updated dimension
   */
  removeResultDescription(id, index) {
    return adminApi.delete(`/dimensions/${id}/result-descriptions/${index}`);
  },

  /**
   * Get dimension data structure for creating/updating dimensions
   * @returns {Object} Default dimension data structure matching the backend DTO
   */
  getDefaultDimensionData() {
    return {
      value: '', // Required string - unique identifier
      translation: {
        en: '', // Required string - English translation
        de: '', // Required string - German translation
      },
      description: {
        en: '', // Required string - English description
        de: '', // Required string - German description
      },
      resultDescriptions: [], // Optional array of result descriptions
    };
  },

  /**
   * Get default result description structure
   * @returns {Object} Default result description data structure
   */
  getDefaultResultDescriptionData() {
    return {
      headline: {
        en: '', // Required string - English headline
        de: '', // Required string - German headline
      },
      content: {
        en: '', // Required string - English content
        de: '', // Required string - German content
      },
      tip: {
        en: '', // Required string - English tip
        de: '', // Required string - German tip
      },
      status: 'positive', // Required string - 'positive' or 'negative'
    };
  },

  /**
   * Validate dimension data before sending to the server
   * @param {Object} dimensionData - The dimension data to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateDimensionData(dimensionData) {
    const errors = [];

    // Validate value
    if (!dimensionData.value || typeof dimensionData.value !== 'string') {
      errors.push('value must be a non-empty string');
    }

    // Validate translation
    if (!dimensionData.translation || typeof dimensionData.translation !== 'object') {
      errors.push('translation must be an object');
    } else {
      if (typeof dimensionData.translation.en !== 'string') {
        errors.push('translation.en must be a string');
      }
      if (typeof dimensionData.translation.de !== 'string') {
        errors.push('translation.de must be a string');
      }
    }

    // Validate description
    if (!dimensionData.description || typeof dimensionData.description !== 'object') {
      errors.push('description must be an object');
    } else {
      if (typeof dimensionData.description.en !== 'string') {
        errors.push('description.en must be a string');
      }
      if (typeof dimensionData.description.de !== 'string') {
        errors.push('description.de must be a string');
      }
    }

    // Validate resultDescriptions (optional)
    if (dimensionData.resultDescriptions !== undefined) {
      if (!Array.isArray(dimensionData.resultDescriptions)) {
        errors.push('resultDescriptions must be an array');
      } else {
        dimensionData.resultDescriptions.forEach((resultDesc, index) => {
          if (!resultDesc.headline || typeof resultDesc.headline !== 'object') {
            errors.push(`resultDescriptions[${index}].headline must be an object`);
          } else {
            if (typeof resultDesc.headline.en !== 'string') {
              errors.push(`resultDescriptions[${index}].headline.en must be a string`);
            }
            if (typeof resultDesc.headline.de !== 'string') {
              errors.push(`resultDescriptions[${index}].headline.de must be a string`);
            }
          }

          if (!resultDesc.content || typeof resultDesc.content !== 'object') {
            errors.push(`resultDescriptions[${index}].content must be an object`);
          } else {
            if (typeof resultDesc.content.en !== 'string') {
              errors.push(`resultDescriptions[${index}].content.en must be a string`);
            }
            if (typeof resultDesc.content.de !== 'string') {
              errors.push(`resultDescriptions[${index}].content.de must be a string`);
            }
          }

          if (!resultDesc.tip || typeof resultDesc.tip !== 'object') {
            errors.push(`resultDescriptions[${index}].tip must be an object`);
          } else {
            if (typeof resultDesc.tip.en !== 'string') {
              errors.push(`resultDescriptions[${index}].tip.en must be a string`);
            }
            if (typeof resultDesc.tip.de !== 'string') {
              errors.push(`resultDescriptions[${index}].tip.de must be a string`);
            }
          }

          if (!['positive', 'negative'].includes(resultDesc.status)) {
            errors.push(`resultDescriptions[${index}].status must be 'positive' or 'negative'`);
          }
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Validate result description data
   * @param {Object} resultDescription - The result description data to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateResultDescriptionData(resultDescription) {
    const errors = [];

    // Validate headline
    if (!resultDescription.headline || typeof resultDescription.headline !== 'object') {
      errors.push('headline must be an object');
    } else {
      if (typeof resultDescription.headline.en !== 'string') {
        errors.push('headline.en must be a string');
      }
      if (typeof resultDescription.headline.de !== 'string') {
        errors.push('headline.de must be a string');
      }
    }

    // Validate content
    if (!resultDescription.content || typeof resultDescription.content !== 'object') {
      errors.push('content must be an object');
    } else {
      if (typeof resultDescription.content.en !== 'string') {
        errors.push('content.en must be a string');
      }
      if (typeof resultDescription.content.de !== 'string') {
        errors.push('content.de must be a string');
      }
    }

    // Validate tip
    if (!resultDescription.tip || typeof resultDescription.tip !== 'object') {
      errors.push('tip must be an object');
    } else {
      if (typeof resultDescription.tip.en !== 'string') {
        errors.push('tip.en must be a string');
      }
      if (typeof resultDescription.tip.de !== 'string') {
        errors.push('tip.de must be a string');
      }
    }

    // Validate status
    if (!['positive', 'negative'].includes(resultDescription.status)) {
      errors.push('status must be "positive" or "negative"');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Search dimensions by value or translation
   * @param {string} searchTerm - The search term
   * @returns {Promise<Array>} Array of matching dimensions
   */
  async searchDimensions(searchTerm) {
    const allDimensions = await this.getAllDimensions();
    const searchLower = searchTerm.toLowerCase();

    return allDimensions.filter(
      (dimension) => dimension.value.toLowerCase().includes(searchLower) ||
        dimension.translation.en.toLowerCase().includes(searchLower) ||
        dimension.translation.de.toLowerCase().includes(searchLower) ||
        dimension.description.en.toLowerCase().includes(searchLower) ||
        dimension.description.de.toLowerCase().includes(searchLower),
    );
  },

  /**
   * Get dimensions by status (positive/negative result descriptions)
   * @param {string} status - The status to filter by ('positive' or 'negative')
   * @returns {Promise<Array>} Array of dimensions with the specified status
   */
  async getDimensionsByStatus(status) {
    const allDimensions = await this.getAllDimensions();

    return allDimensions.filter(
      (dimension) => dimension.resultDescriptions &&
        dimension.resultDescriptions.some((desc) => desc.status === status),
    );
  },

  /**
   * Get all questions for a dimension
   * @param {string} id - The dimension ID
   * @returns {Promise<Array>} Array of questions
   */
  getDimensionQuestions(id) {
    return adminApi.get(`/dimensions/${id}/questions`);
  },

  /**
   * Get a specific question from a dimension
   * @param {string} id - The dimension ID
   * @param {number} questionIndex - The index of the question
   * @returns {Promise<Object>} The question data
   */
  getDimensionQuestion(id, questionIndex) {
    return adminApi.get(`/dimensions/${id}/questions/${questionIndex}`);
  },

  /**
   * Add a question to a dimension
   * @param {string} id - The dimension ID
   * @param {Object} questionData - The question data to add
   * @returns {Promise<Object>} The updated dimension
   */
  addDimensionQuestion(id, questionData) {
    // Remove _id from questionData as backend doesn't expect it for new questions
    const { _id, ...cleanQuestionData } = questionData;
    return adminApi.post(`/dimensions/${id}/questions`, cleanQuestionData);
  },

  /**
   * Update a specific question in a dimension
   * @param {string} id - The dimension ID
   * @param {number} questionIndex - The index of the question to update
   * @param {Object} questionData - The updated question data
   * @returns {Promise<Object>} The updated dimension
   */
  updateDimensionQuestion(id, questionIndex, questionData) {
    // Strip to only fields allowed by UpdateDimensionQuestionDto (forbidNonWhitelisted)
    const props = questionData.properties || {};
    const cleanProps = {};
    if (props.dimensionName !== undefined) cleanProps.dimensionName = props.dimensionName;
    if (props.dimension !== undefined) cleanProps.dimension = props.dimension;
    if (props.content !== undefined) cleanProps.content = props.content;
    if (props.options !== undefined) cleanProps.options = props.options;
    if (props.data !== undefined) cleanProps.data = props.data;
    const cleanQuestionData = {};
    if (questionData.class !== undefined) cleanQuestionData.class = questionData.class;
    if (questionData.type !== undefined) cleanQuestionData.type = questionData.type;
    if (questionData.viewTimeLogs !== undefined) {
      cleanQuestionData.viewTimeLogs = questionData.viewTimeLogs;
    }
    if (questionData.sticky !== undefined) cleanQuestionData.sticky = questionData.sticky;
    cleanQuestionData.properties = cleanProps;
    return adminApi.put(`/dimensions/${id}/questions/${questionIndex}`, cleanQuestionData);
  },

  /**
   * Remove a question from a dimension
   * @param {string} id - The dimension ID
   * @param {number} questionIndex - The index of the question to remove
   * @returns {Promise<Object>} The updated dimension
   */
  removeDimensionQuestion(id, questionIndex) {
    return adminApi.delete(`/dimensions/${id}/questions/${questionIndex}`);
  },

  /**
   * Update all questions for a dimension
   * @param {string} id - The dimension ID
   * @param {Array} questions - Array of questions to set
   * @returns {Promise<Object>} The updated dimension
   */
  updateDimensionQuestions(id, questions) {
    return adminApi.put(`/dimensions/${id}/questions/bulk`, questions);
  },
};
